import { memo } from 'react'

import { ListItemIcon, Menu, MenuItem } from '@mui/material'
import {
  ExpandMoreOutlined,
  ErrorOutlineOutlined,
  HelpOutlineOutlined,
  NotificationImportantOutlined,
} from '@mui/icons-material'

import { Chip, ChipProps, usePopoverState } from '@collaborate-health/uikit'

import { AnnouncementSeverity, AnnouncementSeverityDisplayName } from '../../enums'

const AnnouncementSeverityToColorMap: Record<AnnouncementSeverity, ChipProps['color']> = {
  [AnnouncementSeverity.Information]: 'primary',
  [AnnouncementSeverity.Notification]: 'warning',
  [AnnouncementSeverity.Alert]: 'error',
}

const AnnouncementSeverityToIconMap: Record<AnnouncementSeverity, ChipProps['icon']> = {
  [AnnouncementSeverity.Information]: <HelpOutlineOutlined color='primary' />,
  [AnnouncementSeverity.Notification]: <NotificationImportantOutlined color='warning' />,
  [AnnouncementSeverity.Alert]: <ErrorOutlineOutlined color='error' />,
}

export interface AnnouncementSeverityChipProps {
  severity: AnnouncementSeverity
  onChange?: (severity: AnnouncementSeverity) => void
}

const AnnouncementSeverityChip: React.FC<AnnouncementSeverityChipProps> = ({
  severity = AnnouncementSeverity.Information,
  onChange,
}) => {
  const [anchorRef, open, onOpen, onClose] = usePopoverState<HTMLDivElement>()

  return (
    <>
      <Chip
        ref={anchorRef}
        size='small'
        icon={AnnouncementSeverityToIconMap[severity]}
        color={AnnouncementSeverityToColorMap[severity]}
        label={AnnouncementSeverityDisplayName[severity]}
        onClick={onChange && onOpen}
        onDelete={onChange && onOpen}
        deleteIcon={<ExpandMoreOutlined />}
      />
      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {[AnnouncementSeverity.Information, AnnouncementSeverity.Notification, AnnouncementSeverity.Alert].map(
          (value) => (
            <MenuItem
              key={value}
              selected={severity === value}
              onClick={() => {
                onClose()
                onChange?.(value)
              }}
            >
              <ListItemIcon>{AnnouncementSeverityToIconMap[value]}</ListItemIcon>
              {AnnouncementSeverityDisplayName[value]}
            </MenuItem>
          )
        )}
      </Menu>
    </>
  )
}

export default memo(AnnouncementSeverityChip)

